<template>
  <div class="d-page-wrapper">
    <div class="d-page-toolbar">
      <d-search-input
        v-model="search"
        @submit="onSearchSubmit"
      ></d-search-input>
      <v-btn
        v-text="'+ ' + $t('vehicle.createVehicle')"
        class="d-btn-1 ml-auto"
        :color="currentDealerId ? '' : 'grey lighten-1'"
        depressed
        @click="openLicenseNumberDialog"
      ></v-btn>
    </div>
    <v-data-table
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :headers="tableHeaders"
      :items="tableItems"
      :page="pageNumber"
      :items-per-page="pageSize"
      :server-items-length="totalCount"
      must-sort
      hide-default-footer
      class="d-data-table d-data-table--rows-clickable"
      @page-count="pageCount = $event"
      @click:row="openEditDialog"
    >
      <template v-slot:[`item.licenseNumber`]="{ item }">
        <v-icon class="d-data-table-cell-icon">mdi-car-settings</v-icon>
        {{ item.licenseNumber }}
      </template>
      <template v-slot:[`item.lastTreatment`]="{ item }">
        <template v-if="item.lastTreatment">{{ dateTimeUtil.utcToLocalDisplayDateFormat(item.lastTreatment) }}</template>
      </template>
      <template v-slot:[`header.isNotSendNotification`]>
        <span class="d-header-wrap">{{ $t('vehicle.table.doNotSendNotification') }}</span>
      </template>
      <template v-slot:[`item.isNotSendNotification`]="{ item }">
        <div class="d-not-send-cell" @click.stop>
          <v-simple-checkbox
            :value="!!item.isNotSendNotification"
            :disabled="notSendUpdatingId === item.id"
            color="primary"
            @input="onNotSendNotificationChange(item, $event)"
          ></v-simple-checkbox>
        </div>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="text-no-wrap d-visible-on-row-hover">
          <d-btn-icon type="edit" @click="openEditDialog(item)"></d-btn-icon>
          <d-btn-icon type="delete" @click="deleteVehicle_(item)"></d-btn-icon>
        </div>
      </template>
    </v-data-table>

    <v-pagination
      v-model="pageNumber"
      :length="pageCount"
      :total-visible="15"
      class="my-5"
      @input="onPageInput"
    ></v-pagination>

    <vehicle-license-number-dialog
      :dialog.sync="licenseNumberDialog"
      @create="openCreateDialog"
      @edit="openEditDialog"
      @moved="onVehicleMoved"
    ></vehicle-license-number-dialog>

    <vehicle-create-dialog
      :dialog.sync="createDialog"
      :license-number="createDialog_licenseNumber"
      :vin-number="createDialog_vinNumber"
      @create="openEditDialog"
    ></vehicle-create-dialog>

    <vehicle-edit-dialog
      :dialog.sync="editDialog"
      :vehicle="editDialog_vehicle"
    ></vehicle-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'
import { vehiclesPageSize } from '@/constants'

import pageMixin from '@/mixins/page.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'

import dateTimeUtil from '@/utils/dateTime.util'

import DSearchInput from '@/components/DSearchInput'
import DBtnIcon from '@/components/DBtnIcon'
import VehicleLicenseNumberDialog from './VehicleLicenseNumberDialog'
import VehicleCreateDialog from './VehicleCreateDialog'
import VehicleEditDialog from './VehicleEditDialog/index'


export default {
  mixins: [
    pageMixin,
    snackbarMixin
  ],


  components: {
    DSearchInput,
    DBtnIcon,
    VehicleLicenseNumberDialog,
    VehicleCreateDialog,
    VehicleEditDialog
  },


  data () {
    return {
      dateTimeUtil,
      sortBy: 'lastTreatment',
      sortDesc: true,
      skipSortReload: true,
      search: null,
      pageCount: 0,
      pageNumber: 1,
      pageSize: vehiclesPageSize,
      licenseNumberDialog: false,
      createDialog: false,
      createDialog_licenseNumber: '',
      createDialog_vinNumber: '',
      editDialog: false,
      editDialog_vehicle: null,
      notSendUpdatingId: null
    }
  },


  computed: {
    ...mapGetters('dealer', ['currentDealerId', 'previousDealerId']),
    ...mapGetters('vehicle', ['vehiclesTableItems', 'vehiclesTotalCount']),
    ...mapGetters('user', ['currentUser', 'currentUserIsChainAdmin']),
    ...mapGetters('chain', ['currentChainId']),

    tableHeaders () {
      return [
        { key: 'licenseNumber', text: this.$t('vehicle.table.licenseNumber'), value: 'licenseNumber', cellClass: 'font-weight-medium text-no-wrap' },
        { key: 'vinNumber', text: this.$t('vehicle.table.vinNumber'), value: 'vinNumber', cellClass: 'font-weight-medium text-no-wrap' },
        { key: 'brand', text: this.$t('vehicle.table.brand'), value: 'brand' },
        { key: 'model', text: this.$t('vehicle.table.model'), value: 'model' },
        { key: 'firstName', text: this.$t('vehicle.table.firstName'), value: 'firstName' },
        { key: 'lastName', text: this.$t('vehicle.table.lastName'), value: 'lastName' },
        { key: 'address', text: this.$t('vehicle.table.address'), value: 'address' },
        { key: 'zipCode', text: this.$t('vehicle.table.zipCode'), value: 'zipCode' },
        { key: 'area', text: this.$t('vehicle.table.area'), value: 'area' },
        { key: 'lastTreatment', text: this.$t('vehicle.table.lastTreatment'), value: 'lastTreatment' },
        { key: 'isNotSendNotification', text: this.$t('vehicle.table.doNotSendNotification'), value: 'isNotSendNotification', align: 'center', width: 1, sortable: false, class: 'px-1', cellClass: 'px-1' },
        { key: 'actions', text: '', value: 'actions', align: 'end', width: 1, sortable: false, filterable: false }
      ]
    },

    tableItems () {
      return this.vehiclesTableItems(this.currentDealerId)
    },

    totalCount () {
      return this.vehiclesTotalCount(this.currentDealerId)
    },

    sortQueryKey () {
      const sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
      const sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc
      return `${sortBy || 'lastTreatment'}:${sortDesc ? 'desc' : 'asc'}`
    }
  },


  watch: {
    async currentDealerId () {
      this.restoreInitialVehiclesTableItems(this.previousDealerId)
      this.search = null
      await this.reloadTableItems(1, true)
    },
    async currentChainId () {
      this.restoreInitialVehiclesTableItems(this.previousDealerId)
      this.search = null
      await this.reloadTableItems(1, true)
    },
    sortQueryKey () {
      if (this.skipSortReload) return
      this.reloadTableItems(1)
    }
  },


  methods: {
    ...mapActions('vehicle', ['loadVehiclesTableItems', 'restoreInitialVehiclesTableItems', 'deleteVehicle', 'updateVehicle', 'loadVehicleBrands']),
    ...mapActions('user', ['loadUsersByChainId']),


    openLicenseNumberDialog () {
      if (!this.currentDealerId) {
        this.showSnackbar(this.$t('vehicle.createVehicleDisabledTooltip'), 3000)
      } else {
        this.licenseNumberDialog = true
      }
    },

    openCreateDialog ({ licenseNumber, vinNumber }) {
      this.createDialog_licenseNumber = licenseNumber
      this.createDialog_vinNumber = vinNumber
      this.createDialog = true
    },

    openEditDialog (vehicle, tableRow) {
      const target = tableRow && tableRow.event && tableRow.event.target

      if (target && target.closest && target.closest('.d-not-send-cell, .v-simple-checkbox')) {
        return
      }

      this.editDialog_vehicle = vehicle
      this.editDialog = true
    },

    async onNotSendNotificationChange (vehicle, value) {
      const previous = !!vehicle.isNotSendNotification
      if (previous === value || this.notSendUpdatingId) return

      this.notSendUpdatingId = vehicle.id
      this.$set(vehicle, 'isNotSendNotification', value)

      try {
        await this.updateVehicle({
          ...vehicle,
          isNotSendNotification: value
        })
      } catch (error) {
        this.$set(vehicle, 'isNotSendNotification', previous)
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.notSendUpdatingId = null
      }
    },

    onVehicleMoved (vehicle) {
      this.reloadTableItems()
    },

    onSearchSubmit () {
      this.reloadTableItems(1)
    },

    onPageInput () {
      this.reloadTableItems()
    },

    getSortParams () {
      const [sortBy, sortOrder] = this.sortQueryKey.split(':')
      return { sortBy, sortOrder }
    },

    async reloadTableItems (page, isInitial = false) {
      try {
        this.startPageLoading()

        if (page && page !== this.pageNumber) {
          this.pageNumber = page
        }
        const chainId = this.currentUserIsChainAdmin ? this.currentChainId : this.currentUser.chainId
        const items = await this.loadVehiclesTableItems({
          chainId,
          dealerId: this.currentDealerId,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          search: this.search,
          ...this.getSortParams(),
          isInitial
        })

        return items
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async deleteVehicle_ (vehicle) {
      try {
        const confirm = await this.$confirm(this.$t('vehicle.deleteVehicleConfirm', vehicle), { trueText: this.$t('delete') })

        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteVehicle(vehicle)
          this.showSnackbarSuccess(this.$t('vehicle.vehicleDeleted'))

          if (!this.tableItems.length) {
            const page = (this.pageNumber - 1) || 1
            this.reloadTableItems(page)
          }
        }
      } catch (error) {
        this.$log.error(error)
      }
    }
  },


  mounted () {
    this.restoreInitialVehiclesTableItems(this.currentDealerId)
  },


  async created () {
    try {
      this.setPageTitle(this.$t('nav.vehicles'))
      this.startPageLoading()
      if (this.currentUserIsChainAdmin) {
        await this.loadUsersByChainId()
      }
      const chainId = this.currentUserIsChainAdmin ? this.currentChainId : this.currentUser.chainId
      await this.loadVehiclesTableItems({
        chainId,
        dealerId: this.currentDealerId,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        ...this.getSortParams(),
        isInitial: true
      })

      this.loadVehicleBrands()
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
      this.$nextTick(() => {
        this.skipSortReload = false
      })
    }
  }
}
</script>

<style scoped>
.d-not-send-cell {
  display: flex;
  justify-content: center;
  line-height: 0;
}

.d-not-send-cell >>> .v-simple-checkbox {
  margin: 0;
}

.d-header-wrap {
  display: inline-block;
  max-width: 7.5rem;
  white-space: normal;
  line-height: 1.15;
  vertical-align: bottom;
}
</style>
